import type React from "react";
import { type ReactNode, useEffect, useState } from "react";

import { AccessibleButton } from "@/features/accessibility/components/AccessibleButton";

import "./popup.scss";

type AnchorCoordinates = { x: number; y: number };
type AnchorRef = React.RefObject<HTMLElement>;
export type PopupAnchor = AnchorCoordinates | AnchorRef;

type PopupProps = {
  open: boolean;
  onClose: () => void;
  anchor: PopupAnchor;
  children: ReactNode;
  className?: string;
};

export const Popup: React.FC<PopupProps> = (props) => {
  const [anchorPos, setAnchorPos] = useState<AnchorCoordinates | null>(null);
  const [popupWidth, setPopupWidth] = useState<number>(null);

  // Close on Escape key
  useEffect(() => {
    if (!props.open) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, [props.open, props.onClose]);

  // Compute anchor position when popup is open.
  useEffect(() => {
    if (!props.open) return;
    if (props.anchor && "current" in props.anchor) {
      if (props.anchor.current) {
        const rect = props.anchor.current.getBoundingClientRect();
        setPopupWidth(rect.width);
        setAnchorPos({ x: rect.left, y: rect.bottom });
      }
    } else if (
      props.anchor &&
      typeof props.anchor === "object" &&
      "x" in props.anchor
    ) {
      setAnchorPos(props.anchor as AnchorCoordinates);
      setPopupWidth(null);
    }
  }, [props.anchor, props.open]);

  if (!props.open) return null;

  return (
    <AccessibleButton
      className="backdrop"
      onClick={() => {
        props.onClose();
      }}
    >
      <AccessibleButton
        className={`popup ${props.className ?? ""}`}
        style={
          anchorPos
            ? { left: anchorPos.x, top: anchorPos.y, width: popupWidth }
            : {}
        }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {props.children}
      </AccessibleButton>
    </AccessibleButton>
  );
};

export default Popup;
